import React, { useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  LoadingIndicator,
  LoadingOverlay,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import type {
  Contact,
  Role,
} from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { CardContent, CardHeader } from "../molecules/Card";
import { CheckoutContacts } from "./CheckoutContacts";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { Slideout } from "components/molecules/ConfirmInfo/Slideout";
import { Card, CardHeaderDivider } from "components/atoms/Card";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ErrorModal } from "components/molecules/ErrorModal";
import {
  ContactsUnavailableModal,
  isProspectNotQualifiedContactsError,
} from "components/organisms/ContactsUnavailableModal";
import { ProspectUnavailableModal } from "components/organisms/ProspectUnavailableModal";
import { confirmContacts } from "services/contacts";
import { findContact, getPrimaryContact } from "utils/contacts";
import { isEmptyValue, ROLES } from "utils/usageUtils";
import { useContactManagement } from "hooks/useContactManagement";
import { useUsageData } from "utils/swrHooks";
import { useAuthToken } from "hooks/useAuthToken";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";

type CheckoutEditSignersSlideoutProps = {
  onClose: () => void;
  onSaveSuccess?: () => void;
};

const CheckoutEditSignersSlideout = ({
  onClose,
  onSaveSuccess,
}: CheckoutEditSignersSlideoutProps) => {
  const authKey = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const { prospectId, proposalId, userProfile } = useCheckoutContext();

  const {
    prospect,
    error: prospectError,
    isLoading: isProspectLoading,
  } = useUsageData(prospectId);

  const {
    contactList: contacts,
    isContactsLoading,
    contactsError,
    onEditContact,
    onEditContactRole,
    refetch,
    error: contactError,
    setError: setContactError,
    loading: updateContactLoading,
  } = useContactManagement();

  const selectedPrimaryContact = getPrimaryContact(contacts);
  const selectedUtilityContact = findContact(
    contacts,
    ROLES.UTILITYBILLCONTACT
  );
  const selectedSecondaryContact = findContact(
    contacts,
    ROLES.CONTRACTCOSIGNER
  );

  const [error, setError] = useState<{ action?: string; error?: Error }>();
  const [isSaving, setIsSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const contactsHaveInvalidContactInfo = useCallback((): boolean => {
    const requiredContactsInvalid =
      isEmptyValue(selectedPrimaryContact?.customerEmail) ||
      isEmptyValue(selectedPrimaryContact?.customerPrimaryPhone) ||
      isEmptyValue(selectedUtilityContact?.customerEmail) ||
      isEmptyValue(selectedUtilityContact?.customerPrimaryPhone);
    const secondaryInvalid =
      !!selectedSecondaryContact?.contactId &&
      (isEmptyValue(selectedSecondaryContact?.customerEmail) ||
        isEmptyValue(selectedSecondaryContact?.customerPrimaryPhone));

    return requiredContactsInvalid || secondaryInvalid;
  }, [
    selectedPrimaryContact,
    selectedUtilityContact,
    selectedSecondaryContact,
  ]);

  const handleContactUpdate = useCallback(
    async (newContact: Contact, role: Role, currentContact?: Contact) => {
      if (newContact?.contactId !== currentContact?.contactId) {
        setIsNew(!newContact?.contactId);
        await onEditContact({
          ...newContact,
          role,
          ...(role === ROLES.HOMEOWNER && { primary: true }),
        });
      }
    },
    [onEditContact]
  );

  const handleContactRoleUpdate = useCallback(
    async (newContact: Contact, roleToUpdate: Role, newRole: Role) => {
      try {
        await onEditContactRole({ ...newContact, roleToUpdate, newRole });
      } catch (err) {
        setError({ action: "Updating Contact", error: err as Error });
      }
    },
    [onEditContactRole]
  );

  const handleSave = useCallback(async () => {
    if (!selectedPrimaryContact || !selectedUtilityContact) {
      setError({
        action: "Edit Signers",
        error: new Error(
          "You must select a Primary signer and a Utility contact."
        ),
      });
      return;
    }
    if (contactsHaveInvalidContactInfo()) {
      setError({
        action: "Edit Signers",
        error: new Error(
          "All selected contacts must both have an email and phone number."
        ),
      });
      return;
    }
    if (!prospectId) {
      throw new Error("prospectId is undefined");
    }

    const contactsArr = [
      {
        contactId: selectedPrimaryContact.contactId,
        role: ROLES.HOMEOWNER,
      },
      {
        contactId: selectedUtilityContact.contactId,
        role: ROLES.UTILITYBILLCONTACT,
      },
    ];

    if (selectedSecondaryContact?.contactId) {
      if (
        isEmptyValue(selectedSecondaryContact.customerEmail) ||
        isEmptyValue(selectedSecondaryContact.customerPrimaryPhone)
      ) {
        setError({
          action: "Edit Signers",
          error: new Error("You must select a valid secondary signer."),
        });
        return;
      }
      contactsArr.push({
        contactId: selectedSecondaryContact.contactId,
        role: ROLES.CONTRACTCOSIGNER,
      });
    }

    setIsSaving(true);
    try {
      await confirmContacts(
        authKey || proposalToken || "",
        prospectId,
        proposalId,
        contactsArr,
        !!proposalToken
      );
      await refetch();
      onSaveSuccess?.();
      onClose();
    } catch (err) {
      setError({ action: "Edit Signers", error: err as Error });
    } finally {
      setIsSaving(false);
    }
  }, [
    authKey,
    contactsHaveInvalidContactInfo,
    onClose,
    onSaveSuccess,
    proposalId,
    proposalToken,
    prospectId,
    refetch,
    selectedPrimaryContact,
    selectedSecondaryContact,
    selectedUtilityContact,
  ]);

  const buttons = useMemo(
    () =>
      [
        {
          type: "secondary",
          text: "Cancel & Close",
          onClick: onClose,
          disabled: isSaving,
          dataTestId: "edit-signers-cancel",
        },
        {
          type: "primary",
          text: "Save",
          onClick: handleSave,
          disabled: isSaving,
          dataTestId: "edit-signers-save",
        },
      ] as HeaderButton[],
    [handleSave, isSaving, onClose]
  );

  if (prospectError) {
    return (
      <Slideout onClose={onClose}>
        <ProspectUnavailableModal onClose={onClose} />
      </Slideout>
    );
  }

  if (contactsError && isProspectNotQualifiedContactsError(contactsError)) {
    return (
      <Slideout onClose={onClose}>
        <ContactsUnavailableModal
          prospect={prospect}
          userProfile={userProfile}
          errorMessage={contactsError?.response?.body?.message}
        />
      </Slideout>
    );
  }

  if (contactsError) {
    return (
      <Slideout onClose={onClose}>
        <HeaderContentLayout
          title="There was a Problem."
          description="Something went wrong. Please try refreshing the page."
          content={
            <ErrorComponent
              context="CheckoutEditSignersSlideout"
              action="Get Contacts"
              error={contactsError}
            />
          }
        />
      </Slideout>
    );
  }

  if (isProspectLoading || isContactsLoading || isSaving) {
    return (
      <Slideout>
        <LoadingIndicator fullScreen color="black" />
      </Slideout>
    );
  }

  return (
    <Slideout onClose={onClose}>
      <div data-testid="checkout-edit-signers-slideout">
        {error && (
          <ErrorModal
            context="CheckoutEditSignersSlideout"
            error={error.error}
            action={error.action}
            onClose={() => setError(undefined)}
          />
        )}
        {contactError && (
          <ErrorModal
            title={contactError.title}
            context="CheckoutEditSignersSlideout"
            error={contactError.error}
            action={contactError.action}
            onClose={() => setContactError(undefined)}
            onRetry={contactError.retry}
          />
        )}
        <HeaderContentLayout
          title="Edit signers"
          description="You can edit customer’s name and contact information."
          buttons={buttons}
          content={
            <SlideoutContent>
              <Card>
                <CardHeader>
                  <Typography size={tokens.FONT_SIZE_3} fontWeight="medium">
                    Names on Title
                  </Typography>
                </CardHeader>
                <CardHeaderDivider />
                <CardContent>
                  <Typography variant="p" size={tokens.FONT_SIZE_6}>
                    {prospect?.primaryTitle ?? ""}
                    {prospect?.primaryTitle && prospect?.secondaryTitle && ", "}
                    {prospect?.secondaryTitle ?? ""}
                    {!prospect?.primaryTitle && !prospect?.secondaryTitle && (
                      <StyledNoNameOnTitleText>
                        Name on title data not available, please confirm
                        customer is on title.
                      </StyledNoNameOnTitleText>
                    )}
                  </Typography>
                </CardContent>
              </Card>
              <CheckoutContacts
                selectedPrimaryContact={selectedPrimaryContact}
                setSelectedPrimaryContact={async (contact) =>
                  await handleContactUpdate(
                    contact,
                    ROLES.HOMEOWNER,
                    selectedPrimaryContact
                  )
                }
                selectedUtilityContact={selectedUtilityContact}
                setSelectedUtilityContact={async (contact) =>
                  await handleContactUpdate(
                    contact,
                    ROLES.UTILITYBILLCONTACT,
                    selectedUtilityContact
                  )
                }
                selectedSecondaryContact={selectedSecondaryContact}
                setSelectedSecondaryContact={async (contact, remove) => {
                  remove
                    ? await handleContactRoleUpdate(
                        selectedSecondaryContact!,
                        ROLES.CONTRACTCOSIGNER,
                        ROLES.OTHER
                      )
                    : await handleContactUpdate(
                        contact,
                        ROLES.CONTRACTCOSIGNER,
                        selectedSecondaryContact
                      );
                }}
              />
            </SlideoutContent>
          }
        />
        {updateContactLoading && (
          <LoadingOverlay
            message={`${isNew ? "Creating" : "Updating"} contact...`}
          />
        )}
      </div>
    </Slideout>
  );
};

const SlideoutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const StyledNoNameOnTitleText = styled.p`
  font-size: ${tokens.FONT_SIZE_1}px;
  margin: 0;
  line-height: normal;
`;

export { CheckoutEditSignersSlideout };
export type { CheckoutEditSignersSlideoutProps };
